/** biome-ignore-all lint/style/useNamingConvention: Optimizely tracking properties */
'use client';

import { OPTIMIZELY_EVENT_VALUES } from 'providers/OptimizelyProvider/utils';
import { useEffect, useRef, useState } from 'react';
import { OutbrainWidget } from 'react-outbrain-widget';
import { useIntersectionTracking } from '@/app_lib/hooks/useIntersectionTracking';
import { Breakpoints, useMediaQuery } from '@/app_lib/hooks/useMediaQuery';
import { useOutbrainIndex } from '@/app_lib/hooks/useOutbrainIndex';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import { useAdContext } from '@/app_lib/providers/AdProvider';
import { usePaywall } from '@/app_lib/providers/PaywallProvider';
import { HOME_SECTIONS, OutbrainLoadingState } from '@/app_lib/services/Outbrain/config';
import { AnalyticsEvent } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

interface WidgetIdMap {
  [widgetPlacement: string]: {
    [breakpoint: string]: {
      [content: string]: string;
    };
  };
}

const widgetIdMap: WidgetIdMap = {
  footer: {
    desktop: {
      'no-paywall': 'AR_1',
      paywalled: 'AR_2',
    },
    mobile: {
      'no-paywall': 'MB_1',
      paywalled: 'MB_2',
    },
  },
  inline: {
    desktop: {
      Lifestyle: 'AR_5',
      Retail: 'AR_4',
    },
    mobile: {
      Lifestyle: 'MB_5',
      Retail: 'MB_4',
    },
  },
};

export interface OutbrainProps {
  permalink: string;
  placement: 'footer' | 'inline';
  section?: string;
  replacedAdIndex?: number;
}

export const Outbrain = ({ placement, permalink, section, replacedAdIndex }: OutbrainProps) => {
  const { outbrainScriptLoadedState, setOutbrainScriptLoadedState, setRenderOutbrainScript, hideOutbrain } =
    useAdContext();
  const {
    paywallState: { paywallCheck },
  } = usePaywall();

  const [externalWidgetId, setExternalWidgetId] = useState('');
  const [widgetId, setWidgetId] = useState<string | undefined>('');

  const widgetIndex = useOutbrainIndex();
  const desktop = useMediaQuery(Breakpoints.NonHandheld);
  const breakpoint = desktop ? 'desktop' : 'mobile';
  const placementInfo = placement === 'footer' ? 'outbrain' : `in-stream-${replacedAdIndex}`;

  const { optimizely } = useOptimizely();
  const clickTrackedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useIntersectionTracking(
    'outbrain-container',
    { URL: permalink, value: OPTIMIZELY_EVENT_VALUES.defaultAdImpression },
    'saw_outbrain',
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleClick = () => {
      if (clickTrackedRef.current) {
        return;
      }
      clickTrackedRef.current = true;
      trackOptimizelyEvent(optimizely, 'outbrain_click', { URL: permalink, value: 1 });
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [optimizely, permalink]);

  useEffect(() => {
    if (outbrainScriptLoadedState === OutbrainLoadingState.Pending) {
      setRenderOutbrainScript(true);
      setOutbrainScriptLoadedState(OutbrainLoadingState.Loading);
    }
  }, [outbrainScriptLoadedState, setRenderOutbrainScript, setOutbrainScriptLoadedState]);

  useEffect(() => {
    if (outbrainScriptLoadedState === OutbrainLoadingState.Failed) {
      setWidgetId(undefined);
      return;
    }

    if (paywallCheck !== 'pending' && outbrainScriptLoadedState === OutbrainLoadingState.Loaded) {
      if (placement === 'footer' && !hideOutbrain) {
        setWidgetId(widgetIdMap.footer[breakpoint][paywallCheck]);
      } else if (placement === 'inline' && section && HOME_SECTIONS.includes(section)) {
        const id = widgetIdMap.inline[breakpoint][section];
        setWidgetId(id);
        setExternalWidgetId(`${section}Feed_${id}`);
      } else {
        setWidgetId(undefined);
      }
    }
  }, [breakpoint, paywallCheck, placement, section, outbrainScriptLoadedState, hideOutbrain]);

  // NOTE: 525px and 758px is the average height of the outbrain widget on mobile and desktop respectively.
  // NOTE: Changed to 788px and 1137px to prevent CLS since outbrain content is always taller than the placeholder.
  return (
    <div
      className={cn(
        'flex min-h-[788px] md:min-h-[1137px] w-full flex-col items-center transition-all duration-500',
        // Checks for undefined to hold the space while loading to prevent CLS
        widgetId === undefined && 'h-[1px] min-h-[1px] md:min-h-[1px]',
        AnalyticsEvent.ImpressionElement,
      )}
      data-content-placement={placementInfo}
      id='outbrain-container'
      ref={containerRef}
    >
      {widgetId && outbrainScriptLoadedState === OutbrainLoadingState.Loaded && (
        <OutbrainWidget
          dataSrc={permalink}
          dataWidgetId={widgetId}
          externalWidgetId={externalWidgetId || widgetId}
          key={widgetId}
          widgetIndex={widgetIndex}
        />
      )}
    </div>
  );
};
